import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useInfra } from '../infra/context.js';

export type AuthProvider = 'native' | 'federated';

export interface AuthSession {
  userId: string;
  email: string;
  jwt: string;
  masterKey: CryptoKey;
  rsaPriv: CryptoKey;
  rsaPubB64: string;
  saltB64: string;
  provider: AuthProvider;
}

interface AuthContextValue {
  session: AuthSession | null;
  setSession: (session: AuthSession) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { auth } = useInfra();
  const [session, setSessionState] = useState<AuthSession | null>(null);

  const setSession = useCallback((next: AuthSession) => {
    setSessionState(next);
  }, []);

  const signOut = useCallback(async () => {
    const provider = session?.provider ?? 'native';
    setSessionState(null);
    await auth.signOut(provider);
  }, [session, auth]);

  const value = useMemo(() => ({ session, setSession, signOut }), [session, setSession, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function useAuthSession(): AuthSession {
  const { session } = useAuth();
  if (!session) throw new Error('not authenticated');
  return session;
}
